import type { DreadReason, Motivation } from '@hmawiw/shared';
import { useMemo, useState } from 'react';
import { useNow } from '../../hooks/useMedia';
import { useT } from '../../i18n';
import { AGE_BUCKETS, statsOf, type GroupStat, type StatsRange, type Trend } from '../../lib/stats';
import { useCards } from '../../store/cards';
import { useEvents } from '../../store/events';
import { useSettings } from '../../store/settings';
import controls from '../controls.module.css';
import styles from './Stats.module.css';

const RANGES: StatsRange[] = ['4w', '3m', 'all'];
const RANGE_KEY = 'hmawiw-stats-range';

function readRange(): StatsRange {
  try {
    const v = localStorage.getItem(RANGE_KEY);
    return v === '3m' || v === 'all' ? v : '4w';
  } catch {
    return '4w';
  }
}

/** Statistics computed on the device from the cards and the event log; nothing leaves it. */
export function StatsTab() {
  const t = useT();
  const cards = useCards((s) => s.cards);
  const events = useEvents((s) => s.events);
  const now = useNow();
  const columnCount = useSettings((s) => s.columnCount);
  const COLUMNS = useMemo(() => Array.from({ length: columnCount }, (_, i) => i as Motivation), [columnCount]);
  const [range, setRange] = useState<StatsRange>(readRange);
  const s = useMemo(() => statsOf(cards, events, now, range, columnCount), [cards, events, now, range, columnCount]);
  const pick = (r: StatsRange) => {
    setRange(r);
    try {
      localStorage.setItem(RANGE_KEY, r);
    } catch {
      // no storage
    }
  };
  const pct = (part: number, total: number): string => (total === 0 ? '–' : `${Math.round((part / total) * 100)} %`);
  const days = (d: number | null): string => (d === null ? '–' : d < 1 ? '<1' : String(Math.round(d)));
  const weekdayNames = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(t.locale, { weekday: 'narrow' });
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)));
  }, [t.locale]);
  const blockNames = ['0', '4', '8', '12', '16', '20'];
  const weekNames = s.keepingUp.weeks.map((w) => new Date(w.start).toLocaleDateString(t.locale, { day: 'numeric', month: 'numeric' }));
  const delta = (cur: number, prev: number): string => (cur === prev ? '=' : cur > prev ? `+${cur - prev}` : `−${prev - cur}`);
  const shareText = (v: number | null): string => (v === null ? '–' : `${Math.round(v * 100)} %`);
  const groupTable = (rows: GroupStat[], name: (key: number) => string, colour?: (key: number) => string) => (
    <table className={styles.table}>
      <thead>
        <tr>
          <th />
          <th>{t.t('stats.done')}</th>
          <th>{t.t('stats.open')}</th>
          <th>{t.t('stats.median')}</th>
          <th>{t.t('stats.p75')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((g) => (
          <tr key={g.key}>
            <th style={colour ? { color: colour(g.key) } : undefined}>{name(g.key)}</th>
            <td>{g.done}</td>
            <td>{g.open}</td>
            <td>{days(g.medianDays)}</td>
            <td>{days(g.p75Days)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  const trendRow = (label: string, pick: (x: Trend) => number) =>
    s.trend ? (
      <tr>
        <th>{label}</th>
        <td>{pick(s.trend.current)}</td>
        <td>{pick(s.trend.previous)}</td>
        <td>{delta(pick(s.trend.current), pick(s.trend.previous))}</td>
      </tr>
    ) : null;

  return (
    <>
      <p className={styles.hint}>{t.t('stats.hint')}</p>
      <div className={controls.chips} role="group" aria-label={t.t('stats.range')}>
        {RANGES.map((r) => (
          <button key={r} type="button" className={controls.chip} aria-pressed={r === range} onClick={() => pick(r)}>
            {t.t(`stats.range.${r}`)}
          </button>
        ))}
      </div>
      {!s.hasHistory && s.doneCount === 0 ? <p className={styles.empty}>{t.t('stats.empty')}</p> : null}

      <Section title={t.t('stats.dread')}>
        <Tile title={t.t('stats.timeToDone')} hint={t.t('stats.timeToDoneHint')}>
          {groupTable(s.dread.byColumn, (k) => t.column(k as Motivation), (k) => `var(--m${k})`)}
        </Tile>
        <Tile title={t.t('stats.timeToDoneBand')} hint={t.t('stats.timeToDoneBandHint')}>
          {groupTable(s.dread.byBand, (k) => t.band(k as 0 | 1 | 2))}
        </Tile>
        <Tile title={t.t('stats.timeToDoneEnergy')} hint={t.t('stats.timeToDoneEnergyHint')}>
          {groupTable(s.dread.byEnergy, (k) => t.t(`energy.${k as 1 | 2 | 3}`))}
        </Tile>
        <Tile title={t.t('stats.deleted')} hint={t.t('stats.deletedHint')} big={pct(s.dread.deleted, s.dread.deleted + s.doneCount)} sub={t.t('stats.deletedSub', { deleted: s.dread.deleted, done: s.doneCount })} />
        <Tile title={t.t('stats.dreadedRight')} hint={t.t('stats.dreadedRightHint')} big={pct(s.dread.dreadedDoneRight.moved, s.dread.dreadedDoneRight.total)} sub={t.t('stats.ofN', { n: s.dread.dreadedDoneRight.total })} />
        <Tile title={t.t('stats.moves')} hint={t.t('stats.movesHint')} big={`${s.dread.movesRight} → · ${s.dread.movesLeft} ←`} />
        <Tile title={t.t('stats.forecast')} hint={t.t('stats.forecastHint')} big={pct(s.dread.forecast.easier, s.dread.forecast.easier + s.dread.forecast.worse)} sub={t.t('stats.ofN', { n: s.dread.forecast.easier + s.dread.forecast.worse })} />
      </Section>

      <Section title={t.t('stats.lever')}>
        <Tile title={t.t('stats.attempts')} hint={t.t('stats.attemptsHint')} big={`${s.lever.ran} · ${s.lever.stopped}`} sub={t.t('stats.ranStopped')} />
        <Tile title={t.t('stats.feel')} hint={t.t('stats.feelHint')}>
          <Bars values={[s.lever.feel.worse, s.lever.feel.same, s.lever.feel.better]} labels={[t.t('feel.worse'), t.t('feel.same'), t.t('feel.better')]} colors={['var(--m0)', 'var(--m1)', 'var(--m2)']} />
        </Tile>
        <Tile title={t.t('stats.followed')} hint={t.t('stats.followedHint')} big={pct(s.lever.followed.yes, s.lever.followed.total)} sub={t.t('stats.ofN', { n: s.lever.followed.total })} />
        <Tile title={t.t('stats.momentOpened')} hint={t.t('stats.momentOpenedHint')} big={pct(s.levers.moment.opened, s.levers.moment.shown)} sub={t.t('stats.momentSub', { opened: s.levers.moment.opened, shown: s.levers.moment.shown, done: s.levers.moment.doneAfter })} />
        <Tile title={t.t('stats.morningDone')} hint={t.t('stats.morningDoneHint')} big={pct(s.levers.morning.doneSameDay, s.levers.morning.pinned)} sub={t.t('stats.ofN', { n: s.levers.morning.pinned })} />
        <Tile title={t.t('stats.dreadReasons')} hint={t.t('stats.dreadReasonsHint')}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th />
                <th>{t.t('stats.cards')}</th>
                <th>{t.t('stats.movedOn')}</th>
              </tr>
            </thead>
            <tbody>
              {s.levers.dread.map((r) => (
                <tr key={r.reason}>
                  <th>{t.t(`dread.${r.reason as DreadReason}`)}</th>
                  <td>{r.cards}</td>
                  <td>{r.movedOn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tile>
      </Section>

      <Section title={t.t('stats.when')}>
        <Tile title={t.t('stats.byWeekday')}>
          <Bars values={s.when.byWeekday} labels={weekdayNames} />
        </Tile>
        <Tile title={t.t('stats.byHour')} hint={t.t('stats.byHourHint')}>
          <Bars values={s.when.byBlock} labels={blockNames} />
        </Tile>
        <Tile title={t.t('stats.energyAtDone')} hint={t.t('stats.energyAtDoneHint')}>
          <Bars values={[s.when.energyAtDone.below, s.when.energyAtDone.atOrAbove, s.when.energyAtDone.unset]} labels={[t.t('stats.below'), t.t('stats.atOrAbove'), t.t('lens.none')]} colors={['var(--m1)', 'var(--m2)', 'var(--muted)']} />
        </Tile>
        <Tile title={t.t('stats.energyByHour')} hint={t.t('stats.energyByHourHint')}>
          <Stacked rows={s.when.energyByBlock} labels={blockNames} legend={[t.t('energy.1'), t.t('energy.2'), t.t('energy.3')]} />
        </Tile>
      </Section>

      <Section title={t.t('stats.keepingUp')}>
        {s.trend ? (
          <Tile title={t.t('stats.trend')} hint={t.t('stats.trendHint', { period: t.t(`stats.range.${range}`) })}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th />
                  <th>{t.t('stats.now')}</th>
                  <th>{t.t('stats.before')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {trendRow(t.t('stats.done'), (x) => x.done)}
                {trendRow(t.t('stats.created'), (x) => x.created)}
                {trendRow(t.t('stats.deletedRow'), (x) => x.deleted)}
                {trendRow(t.t('stats.movesRightRow'), (x) => x.movesRight)}
                <tr>
                  <th>{t.t('stats.forecast')}</th>
                  <td>{shareText(s.trend.current.easierShare)}</td>
                  <td>{shareText(s.trend.previous.easierShare)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </Tile>
        ) : null}
        <Tile title={t.t('stats.perWeek')} hint={t.t('stats.perWeekHint')}>
          <Stacked rows={[s.keepingUp.weeks.map((w) => w.done), s.keepingUp.weeks.map((w) => w.created)]} labels={weekNames} legend={[t.t('stats.done'), t.t('stats.created')]} colors={['var(--accent)', 'var(--line)']} side />
        </Tile>
        <Tile title={t.t('stats.openAge')} hint={t.t('stats.openAgeHint')}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th />
                {COLUMNS.map((m) => (
                  <th key={m} style={{ color: `var(--m${m})` }}>
                    {t.column(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {AGE_BUCKETS.map((b, i) => (
                <tr key={b}>
                  <th>{t.t(`stats.age.${b}`)}</th>
                  {COLUMNS.map((m) => (
                    <td key={m}>{s.keepingUp.openAge[i]?.[m] ?? 0}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Tile>
        <Tile title={t.t('stats.late')} hint={t.t('stats.lateHint')} big={pct(s.keepingUp.late.late, s.keepingUp.late.dated)} sub={t.t('stats.ofN', { n: s.keepingUp.late.dated })} />
        <Tile title={t.t('stats.recurring')} hint={t.t('stats.recurringHint')} big={pct(s.keepingUp.recurring.onTime, s.keepingUp.recurring.total)} sub={t.t('stats.ofN', { n: s.keepingUp.recurring.total })} />
        <Tile title={t.t('stats.ongoing')} hint={t.t('stats.ongoingHint')} big={pct(s.keepingUp.ongoing.within, s.keepingUp.ongoing.gaps)} sub={t.t('stats.ofN', { n: s.keepingUp.ongoing.gaps })} />
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <div className={styles.grid}>{children}</div>
    </section>
  );
}

function Tile({ title, hint, big, sub, children }: { title: string; hint?: string; big?: string; sub?: string; children?: React.ReactNode }) {
  return (
    <div className={styles.tile} data-tile={title}>
      <div className={styles.tileTitle}>{title}</div>
      {big !== undefined ? <div className={`display ${styles.big}`}>{big}</div> : null}
      {sub ? <div className={styles.sub}>{sub}</div> : null}
      {children}
      {hint ? <div className={styles.tileHint}>{hint}</div> : null}
    </div>
  );
}

/** One bar per value, drawn as SVG so it follows the theme and needs no library. */
function Bars({ values, labels, colors }: { values: number[]; labels: string[]; colors?: string[] }) {
  const max = Math.max(1, ...values);
  const w = 100 / values.length;
  return (
    <div className={styles.chart}>
      <svg viewBox={`0 0 100 40`} preserveAspectRatio="none" className={styles.svg} aria-hidden="true">
        {values.map((v, i) => {
          const h = (v / max) * 36;
          return <rect key={i} x={i * w + w * 0.15} y={40 - h} width={w * 0.7} height={h} rx={1} fill={colors?.[i] ?? 'var(--accent)'} />;
        })}
      </svg>
      <div className={styles.labels}>
        {values.map((v, i) => (
          <span key={i} className={styles.label}>
            <b>{v}</b>
            {labels[i]}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Rows stacked per label (energy levels by hour), or side by side (done and created per week). */
function Stacked({ rows, labels, legend, colors, side }: { rows: number[][]; labels: string[]; legend: string[]; colors?: string[]; side?: boolean }) {
  const palette = colors ?? ['var(--m0)', 'var(--m1)', 'var(--m2)'];
  const n = labels.length;
  const totals = labels.map((_, i) => rows.reduce((sum, r) => sum + (r[i] ?? 0), 0));
  const max = Math.max(1, ...(side ? rows.flat() : totals));
  const w = 100 / n;
  return (
    <div className={styles.chart}>
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className={styles.svg} aria-hidden="true">
        {labels.map((_, i) => {
          let y = 40;
          return rows.map((r, k) => {
            const v = r[i] ?? 0;
            const h = (v / max) * 36;
            if (side) {
              const bw = (w * 0.7) / rows.length;
              return <rect key={k} x={i * w + w * 0.15 + k * bw} y={40 - h} width={bw} height={h} rx={1} fill={palette[k]} />;
            }
            y -= h;
            return <rect key={k} x={i * w + w * 0.15} y={y} width={w * 0.7} height={h} fill={palette[k]} />;
          });
        })}
      </svg>
      <div className={styles.labels}>
        {labels.map((l, i) => (
          <span key={i} className={styles.label}>
            <b>{side ? (rows[0]?.[i] ?? 0) : totals[i]}</b>
            {l}
          </span>
        ))}
      </div>
      <div className={styles.legend}>
        {legend.map((l, k) => (
          <span key={k}>
            <i style={{ background: palette[k] }} />
            {l}
          </span>
        ))}
      </div>
    </div>
  );
}
